import { Component, type ErrorInfo, type ReactNode } from 'react';
import { LinkButton } from '../ui/Button.js';
import { StateView } from '../ui/StateView.js';
import { clientErrors, newReference, type ClientErrorReporter } from './error-report.js';

export interface RegionErrorBoundaryProps {
  children: ReactNode;
  /** What this region is, for the message: "this page", "the summary". */
  region?: string;
  /** When this changes the boundary resets — a route change recovers a broken page by itself. */
  resetKey?: string;
  /** Compact fallback for a panel within a page. */
  compact?: boolean;
  /**
   * The root boundary, mounted outside the router and every provider (main.tsx): its way back is a plain link, a full
   * navigation to the overview, since there is no router to push to and the shell itself may be what failed.
   */
  bare?: boolean;
  className?: string;
  /** Where the report goes; the application's reporter unless a test says otherwise. */
  reporter?: ClientErrorReporter;
}

interface State {
  error: Error | null;
  reference: string | null;
  resetKey: string | undefined;
}

export interface RegionFailureProps {
  region: string;
  reference: string | null;
  onRetry: () => void;
  compact?: boolean | undefined;
  bare?: boolean | undefined;
  className?: string | undefined;
}

/**
 * The fallback a boundary renders (05-client §5.1): what happened in plain language, a retry, a way back and a
 * reference to quote. Exported so the rendering tests can pin it — a server render cannot make a boundary catch.
 */
export function RegionFailure({ region, reference, onRetry, compact, bare = false, className }: RegionFailureProps) {
  const title = `${region.charAt(0).toUpperCase()}${region.slice(1)} couldn’t be shown`;
  return (
    <StateView
      kind="error"
      title={title}
      description={bare
        ? 'Something in Calis failed to load. Try again, or go back to the overview; if it keeps happening, quote the reference.'
        : 'The rest of Calis still works. Try again; if it keeps happening, quote the reference.'}
      correlationId={reference}
      onRetry={onRetry}
      // Why two kinds of link: inside the router a client-side navigation keeps the shell; at the root there is no
      // router, so the link is a real `<a href>` and the browser reloads the application from the overview.
      action={bare
        ? <LinkButton href="/" variant="ghost">Go to overview</LinkButton>
        : <LinkButton to="/" variant="ghost">Go to overview</LinkButton>}
      compact={compact}
      minHeight={compact ? undefined : bare ? '100dvh' : '40dvh'}
      className={className}
    />
  );
}

/**
 * Catches a rendering error in one region so the navigation survives it (app-baseline 05-client
 * §5.1): what happened in plain language, a retry, a way back, and a reference to quote. The reference is also
 * carried in the report posted to the server (§5.2), so support can open what the person quotes.
 */
export class RegionErrorBoundary extends Component<RegionErrorBoundaryProps, State> {
  override state: State = { error: null, reference: null, resetKey: undefined };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error, reference: newReference() };
  }

  static getDerivedStateFromProps(props: RegionErrorBoundaryProps, state: State): Partial<State> | null {
    if (props.resetKey !== state.resetKey) {
      return { resetKey: props.resetKey, error: null, reference: null };
    }
    return null;
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    const reference = this.state.reference ?? 'ui';
    console.error(`[calis ${reference}] region failed:`, error, info.componentStack);
    // Why: 05-client §5.2 — the reference shown is "carried in the report the app sends to its own telemetry". The
    // reporter is fire-and-forget and never throws, so a failed report cannot become a second failure here.
    void (this.props.reporter ?? clientErrors).report({ kind: 'render', reference, thrown: error, componentStack: info.componentStack ?? null });
  }

  private reset = (): void => {
    this.setState({ error: null, reference: null });
  };

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <RegionFailure
        region={this.props.region ?? 'this part of the page'}
        reference={this.state.reference}
        onRetry={this.reset}
        compact={this.props.compact}
        bare={this.props.bare}
        className={this.props.className}
      />
    );
  }
}
